// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import React, { useContext, useEffect, useState } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { isIOS, isMobile, isAndroid } from '../utils/platform.utils';
const AppStateContext = React.createContext();

export const AppStateProvider = ({ children, useLocalSession }) => {
  const setAppMeetingInfo = (meetingId, name) => {
    setMeeting(meetingId);
    setLocalName(name);
  };
  const [hasNewMessage, setHasNewMessage] = useState(false);

  // Meeting ID
  const [meetingId, setMeeting] = useLocalStorage('meetingId', '');

  // UserName
  const [localUserName, setLocalName] = useLocalStorage('localUserName', '');

  // Theme
  const [theme, setTheme] = useLocalStorage('theme', 'light');

  const toggleTheme = () => setTheme('light' ? 'dark' : 'light');

  const setHasNewMessageIfHidden = (hasNewMessage) => {
    if (!hasNewMessage || document.hidden) {
      setHasNewMessage(hasNewMessage);
    }
  };

  // Device
  const [isIos, setIsIos] = useState(false);
  const [isAndroidDevice, setIsAndroid] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  useEffect(() => {
    const handler = () =>
      document.visibilityState === 'visible' && setHasNewMessage(false);
    document.addEventListener('visibilitychange', handler);

    return () => {
      document.removeEventListener('visibilitychange', handler);
    };
  }, []);

  useEffect(() => {
    const handleDeviceDetection = () => {
      setIsIos(isIOS());
      setIsMobileDevice(isMobile());
      setIsAndroid(isAndroid());
    };

    handleDeviceDetection();
    window.addEventListener('resize', handleDeviceDetection);

    return () => {
      window.removeEventListener('resize', handleDeviceDetection);
    };
  }, []);

  const [_isTempFileSelecting, setIsTempFileSelecting] = useState(false);

  // AndroidでPWAアプリ利用中の状態判定
  useEffect(() => {
    // 可視性変更のハンドラー
    const handleVisibilityChange = () => {
      setIsTempFileSelecting((isTempFileSelecting) => {
        let result = isTempFileSelecting;
        if (document.visibilityState === 'visible') {
          if (isTempFileSelecting) {
            result = false;
          } else {
            // PWAがバックグラウンドから復帰すると一時的にネットワークエラーになりログアウトされることがあるので、リロードを行う（CQA-13648）
            console.log('visible');
            window.location.reload();
          }
        }
        return result;
      });
    };

    // Page Visibility API がサポートされているか確認
    if (
      window.matchMedia('(display-mode: standalone)').matches &&
      isAndroid()
    ) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    // コンポーネントのアンマウント時にイベントリスナーをクリーンアップ
    return () => {
      if (
        window.matchMedia('(display-mode: standalone)').matches &&
        isAndroid()
      ) {
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );
      }
    };
  }, []);

  const providerValue = {
    useLocalSession,
    meetingId,
    localUserName,
    theme,
    hasNewMessage,
    toggleTheme,
    setAppMeetingInfo,
    setHasNewMessage: setHasNewMessageIfHidden,
    isIos,
    isMobileDevice,
    isAndroidDevice,
    setIsTempFileSelecting
  };

  return (
    <AppStateContext.Provider value={providerValue}>
      {children}
    </AppStateContext.Provider>
  );
};

export function useAppState() {
  const state = useContext(AppStateContext);

  if (!state) {
    throw new Error('useAppState must be used within AppStateProvider');
  }

  return state;
}
